import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { ResearchPageDataService } from "../research-page-data.service";
import {
  CompanyLevelLineItems,
  CompanyLevelLineItemsAPIResponse,
  RevenueLineInfo,
  RevenueLineInfoAPIResponse,
  Ticker,
} from "../research-page.model";
import { UntypedFormControl } from "@angular/forms";
import { debounceTime } from "rxjs/operators";
import { AppService } from "src/app/app.service";
import { VAIconRegistryService, search } from "@va/core-library/icons";

export interface AutocompleteOptionGroups {
  title: string;
  id?: string | number;
  children?: AutocompleteOptionGroups[];
}

@Component({
  selector: "app-ticker-selector",
  templateUrl: "./ticker-selector.component.html",
  styleUrls: ["./ticker-selector.component.scss"],
})
export class TickerSelectorComponent implements OnInit {
  selectedTicker;
  @Input() tickerSelectorObj: {
    ticker: string;
    researchDocId: string;
    documentType: string;
  };
  @Input() companies: Array<any>;
  @Output() callback: EventEmitter<any> = new EventEmitter();
  tickerList: Array<Ticker> = [];
  lineItems: CompanyLevelLineItems["lineitems"];
  queryField: UntypedFormControl = new UntypedFormControl();
  optionGroups: AutocompleteOptionGroups[] = [];
  chartData: RevenueLineInfo;
  constructor(
    private dataService: ResearchPageDataService,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([search]);
  }

  ngOnInit(): void {
    this.queryField.valueChanges.pipe(debounceTime(500)).subscribe((text) => {
      if (text !== "") {
        this.searchLineItems(text);
      }
    });
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.companies && changes.companies.currentValue !== undefined) {
      this.getCompanyDetails();
    }
  }

  getCompanyDetails(): void {
    if (this.companies.length === 0) {
      this.selectedTicker = {
        name: "",
        symbologycompanyid: null,
        ticker: "",
      };
      this.triggerChartsError();
    }
    this.dataService
      .getCompanyMapping(
        this.companies.map(function (val) {
          return val.company_id;
        }),
      )
      .subscribe((data: any) => {
        if (data.status === "success") {
          this.tickerList = data.data;
          if (this.tickerList.length) {
            if (this.tickerSelectorObj.ticker !== "") {
              const ticker = this.tickerList.find(
                (t) => t.ticker === this.tickerSelectorObj.ticker,
              );
              if (ticker) {
                this.selectedTicker = ticker;
              } else {
                this.selectedTicker = this.tickerList[0];
              }
            } else {
              this.selectedTicker = this.tickerList[0];
            }
            this.tickerChange();
          }
        }
      });
  }

  searchLineItems(query: string): void {
    this.dataService
      .searchCompanyLevelLineItems(
        encodeURIComponent(query),
        this.selectedTicker.symbologycompanyid,
      )
      .subscribe((data: CompanyLevelLineItemsAPIResponse) => {
        if (data.status === "success") {
          this.lineItems = data.lineitems;
          this.optionGroups = data.lineitems.map((lineItem) => {
            return {
              title: lineItem.tabfullname,
              children: Object.entries(lineItem.subvalues).map(
                ([key, value]) => {
                  return {
                    title: value,
                    id: key,
                  };
                },
              ),
            };
          });
        }
      });
  }
  triggerChartsError() {
    this.lineItems = [];
    this.optionGroups = [];
    this.queryField.setValue("");
    this.chartData = {
      dapparameterid: null,
      line_item_name: "",
      current_quarter: "",
      period: "",
    };
    this.callback.emit({
      chartData: this.chartData,
      ticker: this.selectedTicker,
    });
  }

  tickerChange(): void {
    this.dataService.getDefaultChartData(this.selectedTicker.ticker).subscribe(
      (data: RevenueLineInfoAPIResponse) => {
        if (data.status === "success") {
          this.chartData = data;
          this.queryField.setValue(this.chartData.line_item_name);
          this.callback.emit({
            chartData: this.chartData,
            ticker: this.selectedTicker,
          });
        } else {
          this.triggerChartsError();
        }
      },
      () => {
        this.triggerChartsError();
      },
    );

    const extras = {
      beginning_state_referrer: "File_viewer",
      research_id: this.tickerSelectorObj.researchDocId,
      research_type: this.tickerSelectorObj.documentType,
    };
    this.appService.logUserAction(
      {},
      this.tickerSelectorObj.ticker,
      "",
      "Research_TickerChange",
      extras,
      "",
      "Research_Ticker_Dropdown",
    );
  }

  onSelectLineItem(option: AutocompleteOptionGroups): void {
    this.chartData.dapparameterid = Number(option.id);
    this.chartData.line_item_name = option.title;
    this.callback.emit({
      chartData: this.chartData,
      ticker: this.selectedTicker,
    });
  }
}
